import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import Table from 'Components/UI/Meta/MetaTable/Table';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import metaOverrideSetMap from 'Resources';
import fr from 'facet-rest';
import { withStyles } from '@material-ui/core/styles';
import { QueryVal, removeQuery } from 'Helpers/Query';
import useMetaTable from './useMetaTable';
import MetaQueryForm from '../MetaQueryForm';

const styles = () => ({
  cell: {},
  head: {},
  tableNavigation: {},
  tableContainer: {
    display: 'flex',
    flexGrow: 1,
  },
  filterContainer: {
    display: 'flex',
  },
});

function MetaTable({
  classes,
  collection: collectionProp,
  tableMetaOverrideSetName,
  features,
  hasMetaQueryForm,
  hasSortableColumns,
  linkQueryParamName,
  QueryFormProps,
  onUpdate,
  TableContainerProps,
  disablePagination,
  refreshNowLink,
  ...rest
}) {
  const tableMetaOverrideSet = (tableMetaOverrideSetName) ? metaOverrideSetMap[tableMetaOverrideSetName] : null;
  const {
    columns,
    collection,
    setCollection,
  } = useMetaTable(collectionProp, {
    tableMetaOverrideSet,
    columns: rest.columns,
    linkQueryParamName,
  });

  const refreshNowQuery = QueryVal(refreshNowLink);

  const handleUpdate = (...args) => onUpdate?.call(...args);

  React.useEffect(() => {
    if (collectionProp instanceof fr.Collection) {
      setCollection(collectionProp.overrideMeta(tableMetaOverrideSet));
    }
  }, [collectionProp]);

  const modifiedFeatures = {};

  const refreshCollectionWithLink = async (link) => {
    try {
      let refreshLink = link;
      if (typeof link === 'object') {
        refreshLink = link.url();
      }
      const res = await fr.http.Get(refreshLink);
      const updatedCollection = fr.collection(res, tableMetaOverrideSet);
      setCollection(updatedCollection);
    } catch (err) {
      displayErrorMessage(err, err.errors);
    }
  };

  const refresh = async () => {
    if (!collection) {
      return;
    }
    try {
      const newCollection = await collection.refresh();
      setCollection(newCollection);
    } catch (err) {
      displayErrorMessage(err, err.errors);
    }
  };

  useEffect(() => {
    if (!refreshNowQuery) {
      return;
    }
    refresh().then(() => {
      removeQuery(refreshNowLink);
    });
  }, [refreshNowQuery]);

  if (features.edit) {
    const editFeature = features.edit;
    const originalOnSubmit = editFeature.onSubmit;

    modifiedFeatures.edit = {
      ...features.edit,
      onSubmit: (editedColl) => {
        // Make copy so change is immutable
        const collectionCopy = collection.copy();

        const [updatedResource] = editedColl;

        const collectionResource = collectionCopy.resources().findItem(v => v.id() === updatedResource.id());

        collectionResource.attributes(updatedResource.attributes());

        setCollection(collectionCopy);

        if (originalOnSubmit) {
          originalOnSubmit(editedColl);
        }
        handleUpdate(collectionCopy);
      },
    };
  }

  if (features.create) {
    const createFeature = features.create;
    const originalOnSubmit = createFeature.onSubmit;

    modifiedFeatures.create = {
      ...features.create,
      onSubmit: (createdColl) => {
        const collectionCopy = collection.copy();

        let [createdResource] = createdColl;

        // Apply table meta override to the new resource
        if (tableMetaOverrideSet) {
          createdResource = createdResource.overrideMeta(tableMetaOverrideSet);
        }

        // Add the new resource to the collection
        const collectionResource = collectionCopy.resources().pushItem(createdResource.id(), createdResource);

        collectionResource.attributes(createdResource.attributes());

        setCollection(collectionCopy);

        if (originalOnSubmit) {
          originalOnSubmit(createdColl);
        }
        handleUpdate(collectionCopy);
      },
    };
  }

  if (features.delete) {
    const deleteFeature = features.delete;
    const originalOnSubmit = deleteFeature.onSubmit;

    modifiedFeatures.delete = {
      ...features.delete,
      onSubmit: (res, deletedColl) => {
        const collectionCopy = collection.copy();

        const [deletedResource] = deletedColl;

        // Remove the resource from the collection
        collectionCopy.resources().removeItem(deletedResource.id());

        setCollection(collectionCopy);

        if (originalOnSubmit) {
          originalOnSubmit(deletedColl);
        }
        handleUpdate(collectionCopy);
      },
    };
  }

  if (features.search) {
    const feature = features.search;
    const originalOnSubmit = feature.onSubmit;

    modifiedFeatures.search = {
      ...features.search,
      onSubmit: async (searchString) => {
        try {
          const filteredCollection = await collection.link('self').Get({
            metaOverrideSet: tableMetaOverrideSet,
            params: {
              [feature.searchField]: `like|${searchString}`,
            },
          });

          setCollection(filteredCollection);

          if (originalOnSubmit) {
            await originalOnSubmit(filteredCollection);
          }
        } catch (err) {
          if (feature?.onFailure) {
            feature.onFailure(err);
          }
        }
      },
    };
  }

  modifiedFeatures.onClick = features.onClick;
  modifiedFeatures.isSelected = features.isSelected;

  // This will be empty for a flicker. While state updates.
  // This trade off comes with the benefit of us being able to guarantee to only apply meta overrides on mount of the component.
  if (!collection) {
    return null;
  }

  return (
    <div
      className={classes.tableContainer}
      {...TableContainerProps}
    >
      {hasMetaQueryForm && (
        <div className={classes.filterContainer}>
          <MetaQueryForm
            linkQueryParamName={linkQueryParamName}
            collection={collection}
            onSubmit={refreshCollectionWithLink}
            // metaOverrideSetName
            {...QueryFormProps}
          />
        </div>
      )}
      <Table
        classes={{
          cell: classes.cell,
          head: classes.head,
          tableNavigation: classes.tableNavigation,
        }}
        collection={collection}
        columns={columns}
        features={modifiedFeatures}
        onClickPaginationLink={refreshCollectionWithLink}
        linkQueryParamName={linkQueryParamName}
        onSortUpdate={hasSortableColumns ? refreshCollectionWithLink : null}
        disablePagination={disablePagination}
        {...rest}
      />
    </div>
  );
}

MetaTable.defaultProps = {
  hasMetaQueryForm: false,
  hasSortableColumns: false,
  tableMetaOverrideSet: null,
  linkQueryParamName: 'link',
  QueryFormProps: {},
  TableContainerProps: {},
  onUpdate: null,
  disablePagination: false,
  refreshNowLink: null,
  features: {},
};

MetaTable.propTypes = {
  classes: PropTypes.object.isRequired,
  hasMetaQueryForm: PropTypes.bool,
  hasSortableColumns: PropTypes.bool,
  linkQueryParamName: PropTypes.string,
  collection: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  tableMetaOverrideSet: PropTypes.string,
  QueryFormProps: PropTypes.object,
  TableContainerProps: PropTypes.object,
  onUpdate: PropTypes.func,
  disablePagination: PropTypes.bool,
  refreshNowLink: PropTypes.string,
  features: PropTypes.object,
};

export default withStyles(styles)(MetaTable);
